import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Package, Plus, Search } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRODUCT_CATEGORIES, categoryLabel } from "@/lib/productCategories";

function SortableHead({ field, current, order, onSort, children, className = "" }) {
  const active = current === field;
  const Icon = !active ? ArrowUpDown : order === "asc" ? ArrowUp : ArrowDown;
  return <TableHead className={className}>
    <button type="button" className="flex items-center gap-1.5 whitespace-nowrap hover:text-foreground" onClick={() => onSort(field)}>
      {children}<Icon className="h-3.5 w-3.5" />
    </button>
  </TableHead>;
}

export default function Products() {
  const { t, fmtCurrency, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("true");
  const [productType, setProductType] = useState("all");
  const [category, setCategory] = useState("all");
  const [vehicleMake, setVehicleMake] = useState("all");
  const [vehicleModel, setVehicleModel] = useState("all");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["products", q, page, pageSize, status, productType, category, vehicleMake, vehicleModel, sort, order],
    queryFn: async () => (await api.get("/products", { params: {
      q, page, page_size: pageSize, sort, order,
      is_active: status === "all" ? "" : status,
      product_type: productType === "all" ? "" : productType,
      category: category === "all" ? "" : category,
      vehicle_make: vehicleMake === "all" ? "" : vehicleMake,
      vehicle_model: vehicleModel === "all" ? "" : vehicleModel,
    } })).data,
    placeholderData: keepPreviousData,
  });
  const { data: facets = { vehicle_makes: [], vehicle_models: [] } } = useQuery({
    queryKey: ["product-facets"], queryFn: async () => (await api.get("/products/facets")).data,
  });
  const vehicleModels = facets.vehicle_models.filter((item) => vehicleMake === "all" || item.make === vehicleMake);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const changeSort = (field) => {
    if (field === sort) setOrder((value) => value === "asc" ? "desc" : "asc");
    else { setSort(field); setOrder("asc"); }
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title={t("products.title")}
        subtitle={t("products.subtitle")}
        actions={can("products:write") && (
          <Button onClick={() => navigate("/products/new")} data-testid="new-product-button">
            <Plus className="h-4 w-4" />{t("products.new")}
          </Button>
        )}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={t("common.search")} className="pl-9" data-testid="product-search-input" />
        </div>
        <Select value={status} onValueChange={(value) => { setStatus(value); setPage(1); }}>
          <SelectTrigger className="w-40" aria-label={t("common.status")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="true">{t("common.active")}</SelectItem><SelectItem value="false">{t("common.inactive")}</SelectItem><SelectItem value="all">{t("common.all")}</SelectItem></SelectContent>
        </Select>
        <Select value={productType} onValueChange={(value) => { setProductType(value); setPage(1); }}>
          <SelectTrigger className="w-40" aria-label={t("products.type")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("common.all")}</SelectItem><SelectItem value="product">{t("products.product")}</SelectItem><SelectItem value="service">{t("products.service")}</SelectItem></SelectContent>
        </Select>
        <Select value={category} onValueChange={(value) => { setCategory(value); setPage(1); }}>
          <SelectTrigger className="w-52" aria-label={t("products.category")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("products.all_categories")}</SelectItem>{PRODUCT_CATEGORIES.map((value) => <SelectItem key={value} value={value}>{categoryLabel(t, value)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vehicleMake} onValueChange={(value) => { setVehicleMake(value); setVehicleModel("all"); setPage(1); }}>
          <SelectTrigger className="w-48" aria-label={t("products.vehicle_make")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("products.all_vehicle_makes")}</SelectItem>{facets.vehicle_makes.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vehicleModel} onValueChange={(value) => { setVehicleModel(value); setPage(1); }}>
          <SelectTrigger className="w-48" aria-label={t("products.vehicle_model")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("products.all_vehicle_models")}</SelectItem>{vehicleModels.map((item) => <SelectItem key={`${item.make}-${item.model}`} value={item.model}>{item.make} {item.model}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
          <SelectTrigger className="w-28" aria-label="Einträge pro Seite"><SelectValue /></SelectTrigger>
          <SelectContent>{[25, 50, 100].map((size) => <SelectItem key={size} value={String(size)}>{size} / Seite</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Package}
            title={t("products.empty")}
            testId="products-empty"
            action={can("products:write") && <Button onClick={() => navigate("/products/new")}><Plus className="h-4 w-4" />{t("products.new")}</Button>}
          />
        ) : (
          <Table containerClassName="max-h-[65vh]" data-testid="products-table">
            <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background">
              <TableRow>
                <SortableHead field="sku" current={sort} order={order} onSort={changeSort}>{t("products.sku")}</SortableHead>
                <SortableHead field="name" current={sort} order={order} onSort={changeSort}>{t("common.name")}</SortableHead>
                <SortableHead field="category" current={sort} order={order} onSort={changeSort}>{t("products.category")}</SortableHead>
                <SortableHead field="vehicle_make" current={sort} order={order} onSort={changeSort} className="hidden lg:table-cell">{t("automotive.vehicle")}</SortableHead>
                <SortableHead field="product_type" current={sort} order={order} onSort={changeSort}>{t("products.type")}</SortableHead>
                <SortableHead field="selling_price" current={sort} order={order} onSort={changeSort} className="text-right">{t("products.selling_price")}</SortableHead>
                {can("products:write") && <SortableHead field="margin_pct" current={sort} order={order} onSort={changeSort} className="hidden text-right md:table-cell">{t("products.margin")}</SortableHead>}
                <SortableHead field="is_active" current={sort} order={order} onSort={changeSort}>{t("common.status")}</SortableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/products/${p.id}`)} data-testid={`product-row-${p.id}`}>
                  <TableCell className="font-medium tabular-nums">{p.sku}</TableCell>
                  <TableCell className="font-medium">{locale === "en" && p.name_en ? p.name_en : p.name_de}</TableCell>
                  <TableCell>{categoryLabel(t, p.category)}</TableCell>
                  <TableCell className="hidden lg:table-cell">{p.vehicle_make ? `${p.vehicle_make}${p.vehicle_model ? ` ${p.vehicle_model}` : ""}` : "—"}</TableCell>
                  <TableCell>{t(`products.${p.product_type}`)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCurrency(p.selling_price)}</TableCell>
                  {can("products:write") && <TableCell className="hidden text-right tabular-nums text-slate-500 md:table-cell">{p.margin_pct ? `${p.margin_pct}%` : "—"}</TableCell>}
                  <TableCell><StatusBadge status={p.is_active ? "active" : "inactive"} labelKey={p.is_active ? "common.active" : "common.inactive"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {data && data.total > pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{t("common.total")}: {data.total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => p - 1)} data-testid="products-prev">{t("common.previous")}</Button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)} data-testid="products-next">{t("common.next")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
